/**
 * A cheap record of how much content each measured column holds, used to decide when the
 * column widths must be measured again.
 *
 * EditableTable measures its columns when the visible column set changes, not on every
 * data change (re-measuring on every filter or sort was the old flicker source). A
 * `renderItemFn` can draw from data the table never sees, though: Contacts renders email,
 * phone and address from the resources and addresses props, which usually load after the
 * contacts. The first measurement then sees empty cells, the email and phone columns lock
 * at their header widths, and the content that arrives later overflows into the next
 * column (OWA-1292).
 *
 * The size of a cell is its text length plus its element count, so a cell that gains
 * text or icons grows. It reads no layout, only the DOM tree. The caller records the
 * signature when it measures and compares it after each render; when any column's
 * largest cell has grown, the table measures again. Measurement is growth-only, so a
 * repeat can only widen a column, and recording the signature at each measurement stops
 * the comparison from firing twice for the same content.
 */
export type ContentSignature = Record<string, number>;

export function contentSignature(grid: HTMLElement, labels: ReadonlySet<string>): ContentSignature {
    const signature: ContentSignature = {};
    for (const label of labels) {
        // Only the measured columns' cells: the other columns can be most of a large table.
        const cells = grid.querySelectorAll<HTMLElement>(
            `.editable-table__cell[data-col-label="${CSS.escape(label)}"]:not(.editable-table__header):not(.editable-table__footer)`,
        );
        for (const cell of cells) {
            const size = (cell.textContent?.length ?? 0) + cell.getElementsByTagName('*').length;
            if (size > (signature[label] ?? 0)) signature[label] = size;
        }
    }
    return signature;
}

/** True when any column's largest cell in `next` is larger than it was in `recorded`. */
export function contentGrew(recorded: ContentSignature, next: ContentSignature): boolean {
    return Object.entries(next).some(([label, size]) => size > (recorded[label] ?? 0));
}
